/**
 * WhatsApp Cloud API media rules shared by the web composer (previews, hints, client-side
 * conversions) and the API (upload validation). The accepted list was verified against the
 * Cloud API upload endpoint on 2026-09-24: any other declared type is refused with error #100.
 * The API checks only the declared type, never the bytes, so the rules below decide what we
 * declare.
 */
export type MediaKind = "image" | "video" | "audio" | "document" | "sticker";

/**
 * What the member asked for in the composer. "media" is Fotos e vídeos (inline, compressed),
 * "document" keeps the original file, "audio" is an audio file, "voice" a recorded note and
 * "sticker" a WhatsApp sticker.
 */
export type UploadIntent = "media" | "document" | "audio" | "voice" | "sticker";

export const UPLOAD_INTENTS: readonly UploadIntent[] = [
  "media",
  "document",
  "audio",
  "voice",
  "sticker",
];

export const MB = 1024 * 1024;
export const IMAGE_MAX_BYTES = 5 * MB;
export const VIDEO_MAX_BYTES = 16 * MB;
export const AUDIO_MAX_BYTES = 16 * MB;
export const DOCUMENT_MAX_BYTES = 100 * MB;
export const STICKER_STATIC_MAX_BYTES = 100 * 1024;
export const STICKER_ANIMATED_MAX_BYTES = 500 * 1024;
/** Captions of images, videos and documents. Audio and stickers take none. */
export const CAPTION_MAX_LENGTH = 1024;
/** Side of a WhatsApp sticker, in pixels. */
export const STICKER_SIZE = 512;

export interface AcceptedMedia {
  /** Kind used when the member did not ask for a document. */
  kind: MediaKind;
  maxBytes: number;
  ext: string;
}

/** Declared types the Cloud API accepts. Keys are base mime types (parameters stripped). */
export const WHATSAPP_MEDIA: Readonly<Record<string, AcceptedMedia>> = {
  "image/jpeg": { kind: "image", maxBytes: IMAGE_MAX_BYTES, ext: "jpg" },
  "image/png": { kind: "image", maxBytes: IMAGE_MAX_BYTES, ext: "png" },
  "image/webp": { kind: "sticker", maxBytes: STICKER_ANIMATED_MAX_BYTES, ext: "webp" },
  "video/mp4": { kind: "video", maxBytes: VIDEO_MAX_BYTES, ext: "mp4" },
  "video/3gpp": { kind: "video", maxBytes: VIDEO_MAX_BYTES, ext: "3gp" },
  "audio/aac": { kind: "audio", maxBytes: AUDIO_MAX_BYTES, ext: "aac" },
  "audio/mp4": { kind: "audio", maxBytes: AUDIO_MAX_BYTES, ext: "m4a" },
  "audio/mpeg": { kind: "audio", maxBytes: AUDIO_MAX_BYTES, ext: "mp3" },
  "audio/amr": { kind: "audio", maxBytes: AUDIO_MAX_BYTES, ext: "amr" },
  "audio/ogg": { kind: "audio", maxBytes: AUDIO_MAX_BYTES, ext: "ogg" },
  "audio/opus": { kind: "audio", maxBytes: AUDIO_MAX_BYTES, ext: "opus" },
  "application/pdf": { kind: "document", maxBytes: DOCUMENT_MAX_BYTES, ext: "pdf" },
  "application/msword": { kind: "document", maxBytes: DOCUMENT_MAX_BYTES, ext: "doc" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    kind: "document",
    maxBytes: DOCUMENT_MAX_BYTES,
    ext: "docx",
  },
  "application/vnd.ms-excel": { kind: "document", maxBytes: DOCUMENT_MAX_BYTES, ext: "xls" },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    kind: "document",
    maxBytes: DOCUMENT_MAX_BYTES,
    ext: "xlsx",
  },
  "application/vnd.ms-powerpoint": { kind: "document", maxBytes: DOCUMENT_MAX_BYTES, ext: "ppt" },
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": {
    kind: "document",
    maxBytes: DOCUMENT_MAX_BYTES,
    ext: "pptx",
  },
  "text/plain": { kind: "document", maxBytes: DOCUMENT_MAX_BYTES, ext: "txt" },
};

/** Browsers often report no type (HEIC on Chrome) or a vague one; the extension fills the gap. */
export const MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jfif: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  bmp: "image/bmp",
  heic: "image/heic",
  heif: "image/heif",
  avif: "image/avif",
  tif: "image/tiff",
  tiff: "image/tiff",
  svg: "image/svg+xml",
  ico: "image/x-icon",
  mp4: "video/mp4",
  m4v: "video/x-m4v",
  mov: "video/quicktime",
  webm: "video/webm",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  "3gp": "video/3gpp",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  opus: "audio/opus",
  amr: "audio/amr",
  wav: "audio/wav",
  aif: "audio/aiff",
  aiff: "audio/aiff",
  flac: "audio/flac",
  caf: "audio/x-caf",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  json: "application/json",
  xml: "application/xml",
  md: "text/markdown",
  log: "text/plain",
  yaml: "application/yaml",
  yml: "application/yaml",
  ics: "text/calendar",
  vcf: "text/vcard",
  html: "text/html",
  htm: "text/html",
  zip: "application/zip",
  rar: "application/vnd.rar",
  "7z": "application/x-7z-compressed",
  odt: "application/vnd.oasis.opendocument.text",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  odp: "application/vnd.oasis.opendocument.presentation",
  rtf: "application/rtf",
  key: "application/vnd.apple.keynote",
  pages: "application/vnd.apple.pages",
  numbers: "application/vnd.apple.numbers",
};

/** Plain-text formats the Cloud API does not list: declared as text/plain, original name kept. */
export const TEXT_LIKE_EXTENSIONS: ReadonlySet<string> = new Set([
  "csv",
  "tsv",
  "json",
  "xml",
  "md",
  "markdown",
  "log",
  "yaml",
  "yml",
  "ini",
  "conf",
  "cfg",
  "env",
  "sql",
  "srt",
  "vtt",
  "ics",
  "vcf",
  "html",
  "htm",
  "css",
  "js",
  "ts",
  "sh",
]);

/** Images a browser can decode and redraw as JPEG or PNG before sending as a photo. */
export const BROWSER_CONVERTIBLE_IMAGES: ReadonlySet<string> = new Set([
  "image/webp",
  "image/gif",
  "image/bmp",
  "image/avif",
  "image/svg+xml",
  "image/x-icon",
]);

/** Images that need a dedicated decoder in the browser (loaded on demand). */
export const HEIF_IMAGES: ReadonlySet<string> = new Set(["image/heic", "image/heif"]);

/** Recorders produce these; the API remuxes Opus into OGG for a voice note. */
export const VOICE_RECORDING_TYPES: ReadonlySet<string> = new Set(["audio/webm", "audio/ogg"]);

/** "audio/ogg; codecs=opus" → "audio/ogg". */
export function normalizeMime(mime: string | null | undefined): string {
  return (mime ?? "").split(";")[0]!.trim().toLowerCase();
}

/** "Relatório.Final.PDF" → "pdf"; names without an extension give "". */
export function extensionOf(name: string | null | undefined): string {
  const base = (name ?? "").split(/[\\/]/).pop() ?? "";
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : "";
}

/** The declared type, falling back to the extension when the browser sent none or a generic one. */
export function resolveMime(file: { name: string; type?: string | null }): string {
  const declared = normalizeMime(file.type);
  if (declared && declared !== "application/octet-stream") return declared;
  return MIME_BY_EXTENSION[extensionOf(file.name)] ?? (declared || "application/octet-stream");
}

/**
 * How a file reaches WhatsApp:
 * - "none": sent as it is;
 * - "text_as_plain": plain text declared as text/plain (the API does this);
 * - "image_to_jpeg": redrawn as JPEG or PNG in the browser before upload;
 * - "heif_to_jpeg": decoded in the browser with the HEIF decoder, then as above;
 * - "image_compress": a JPEG or PNG above the photo limit, recompressed in the browser;
 * - "sticker_webp": redrawn as a 512×512 WebP in the browser;
 * - "voice_to_ogg": a recorded note remuxed into OGG/Opus (the API does this).
 */
export type MediaConversion =
  | "none"
  | "text_as_plain"
  | "image_to_jpeg"
  | "heif_to_jpeg"
  | "image_compress"
  | "sticker_webp"
  | "voice_to_ogg";

export type UploadRefusal = "type_not_accepted" | "too_large" | "empty";

export type UploadPlan =
  | {
      ok: true;
      kind: MediaKind;
      /** Type declared to WhatsApp once converted. */
      mime: string;
      maxBytes: number;
      conversion: MediaConversion;
      /** Only voice notes are sent with the voice flag. */
      voice: boolean;
      /** Whether the kind takes a caption. */
      caption: boolean;
    }
  | { ok: false; reason: UploadRefusal; mime: string; maxBytes?: number };

function accepted(
  kind: MediaKind,
  mime: string,
  maxBytes: number,
  conversion: MediaConversion,
  voice = false,
): UploadPlan {
  return {
    ok: true,
    kind,
    mime,
    maxBytes,
    conversion,
    voice,
    caption: kind === "image" || kind === "video" || kind === "document",
  };
}

/**
 * Decides kind, declared type and conversion for a file and the member's intent. The web calls
 * it with the original file (to preview and convert); the API calls it again with what arrived
 * (already converted by the browser), so both sides agree on the outcome.
 */
export function planUpload(
  file: { name: string; type?: string | null; size: number },
  intent: UploadIntent = "media",
): UploadPlan {
  const mime = resolveMime(file);
  const ext = extensionOf(file.name);
  if (file.size <= 0) return { ok: false, reason: "empty", mime };
  const known = WHATSAPP_MEDIA[mime];
  const tooLarge = (maxBytes: number): UploadPlan => ({
    ok: false,
    reason: "too_large",
    mime,
    maxBytes,
  });

  if (intent === "voice") {
    if (VOICE_RECORDING_TYPES.has(mime) || mime === "audio/opus")
      return file.size > AUDIO_MAX_BYTES
        ? tooLarge(AUDIO_MAX_BYTES)
        : accepted(
            "audio",
            "audio/ogg",
            AUDIO_MAX_BYTES,
            mime === "audio/webm" ? "voice_to_ogg" : "none",
            true,
          );
    return { ok: false, reason: "type_not_accepted", mime };
  }

  if (intent === "sticker") {
    // Stickers must be exactly 512×512: the browser always redraws them. What reaches the API
    // is that WebP, which it takes as it is.
    if (mime === "image/webp" && file.size <= STICKER_ANIMATED_MAX_BYTES)
      return accepted("sticker", "image/webp", STICKER_ANIMATED_MAX_BYTES, "sticker_webp");
    if (mime.startsWith("image/") && !HEIF_IMAGES.has(mime))
      return accepted("sticker", "image/webp", STICKER_STATIC_MAX_BYTES, "sticker_webp");
    return { ok: false, reason: "type_not_accepted", mime };
  }

  if (intent === "document") {
    if (known)
      return file.size > DOCUMENT_MAX_BYTES
        ? tooLarge(DOCUMENT_MAX_BYTES)
        : accepted("document", mime, DOCUMENT_MAX_BYTES, "none");
    if (TEXT_LIKE_EXTENSIONS.has(ext) || mime.startsWith("text/"))
      return file.size > DOCUMENT_MAX_BYTES
        ? tooLarge(DOCUMENT_MAX_BYTES)
        : accepted("document", "text/plain", DOCUMENT_MAX_BYTES, "text_as_plain");
    return { ok: false, reason: "type_not_accepted", mime };
  }

  // "media" (Fotos e vídeos, drag and drop, paste) and "audio" behave like WhatsApp Web:
  // photos and videos inline, everything else as a document or an audio file.
  if (mime === "image/jpeg" || mime === "image/png")
    return file.size > IMAGE_MAX_BYTES
      ? accepted("image", "image/jpeg", IMAGE_MAX_BYTES, "image_compress")
      : accepted("image", mime, IMAGE_MAX_BYTES, "none");
  if (BROWSER_CONVERTIBLE_IMAGES.has(mime))
    return accepted("image", "image/jpeg", IMAGE_MAX_BYTES, "image_to_jpeg");
  if (HEIF_IMAGES.has(mime))
    return accepted("image", "image/jpeg", IMAGE_MAX_BYTES, "heif_to_jpeg");
  if (known && known.kind === "video")
    return file.size > VIDEO_MAX_BYTES
      ? tooLarge(VIDEO_MAX_BYTES)
      : accepted("video", mime, VIDEO_MAX_BYTES, "none");
  if (known && known.kind === "audio")
    return file.size > AUDIO_MAX_BYTES
      ? tooLarge(AUDIO_MAX_BYTES)
      : accepted("audio", mime, AUDIO_MAX_BYTES, "none");
  if (intent === "audio") return { ok: false, reason: "type_not_accepted", mime };
  return planUpload(file, "document");
}

/** Human size in the style of WhatsApp: "856 KB", "2,4 MB", "56 MB" (decimal mark of the locale). */
export function formatFileSize(bytes: number, locale = "pt-BR"): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / MB;
  const digits = mb < 10 ? 1 : 0;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(mb)} MB`;
}
